import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Container,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import {
  Refresh,
  Visibility,
  VisibilityOff,
  InfoOutlined,
  EmailOutlined,
  LockOutlined,
  ShieldOutlined,
  AdminPanelSettings,
} from '@mui/icons-material';
import { authAPI, buildAdminUser } from '../../services/api';
import { setAdminToken, setAdminUser } from '../../utils/authSessionStorage';
import { getDefaultRouteForRole } from '../../utils/roleUtils';
import browserGlobal from '../../utils/browserGlobal';
import { useLanguage } from '../../context/LanguageContext';
import logo from '../../assets/logo.png';

const gradientBtnSx = {
  background: 'linear-gradient(135deg, #1b5a5c 0%, #0a2829 100%)',
  '&:hover': {
    background: 'linear-gradient(135deg, #328284 0%, #0F3D3E 100%)',
  },
};

const generateCaptchaCode = () => {
  const chars = '23456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz';
  let result = '';
  for (let i = 0; i < 6; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

const Login = () => {
  const { t } = useLanguage();
  const [credentials, setCredentials] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [successBanner, setSuccessBanner] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const canvasRef = useRef(null);
  const [captchaCode, setCaptchaCode] = useState('');
  const [captchaInput, setCaptchaInput] = useState('');

  const refreshCaptcha = useCallback(() => {
    const newCode = generateCaptchaCode();
    setCaptchaCode(newCode);
    setCaptchaInput('');
  }, []);

  useEffect(() => {
    refreshCaptcha();
  }, [refreshCaptcha]);

  useEffect(() => {
    if (!canvasRef.current || !captchaCode) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Background gradient
    const bgGradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
    bgGradient.addColorStop(0, '#f0f4f8');
    bgGradient.addColorStop(1, '#e2e8f0');
    ctx.fillStyle = bgGradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Noise lines (theme color)
    for (let i = 0; i < 5; i++) {
      ctx.strokeStyle = `rgba(15, 61, 62, ${0.2 + Math.random() * 0.25})`;
      ctx.lineWidth = 1.2;
      ctx.beginPath();
      ctx.moveTo(Math.random() * canvas.width, Math.random() * canvas.height);
      ctx.lineTo(Math.random() * canvas.width, Math.random() * canvas.height);
      ctx.stroke();
    }

    // Noise dots
    for (let i = 0; i < 25; i++) {
      ctx.fillStyle = `rgba(15, 61, 62, ${Math.random() * 0.25})`;
      ctx.beginPath();
      ctx.arc(Math.random() * canvas.width, Math.random() * canvas.height, 1.2, 0, Math.PI * 2);
      ctx.fill();
    }

    // Draw rotated captcha characters (theme palette)
    ctx.font = 'bold 20px Roboto, sans-serif';
    ctx.textBaseline = 'middle';

    const themeColors = ['#0F3D3E', '#1b5a5c', '#0a2829', '#328284', '#2d3748'];
    const charWidth = (canvas.width - 20) / captchaCode.length;
    for (let i = 0; i < captchaCode.length; i++) {
      ctx.save();
      const x = 12 + i * charWidth;
      const y = canvas.height / 2 + (Math.random() * 3 - 1.5);
      const angle = (Math.random() - 0.5) * 0.25;
      ctx.translate(x, y);
      ctx.rotate(angle);
      ctx.fillStyle = themeColors[i % themeColors.length];
      ctx.fillText(captchaCode[i], 0, 0);
      ctx.restore();
    }
  }, [captchaCode]);

  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotStep, setForgotStep] = useState(1);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotOtp, setForgotOtp] = useState('');
  const [forgotNewPass, setForgotNewPass] = useState('');
  const [forgotConfirmPass, setForgotConfirmPass] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState('');
  const [forgotInfo, setForgotInfo] = useState('');

  const getT = (key, fallback) => {
    const res = t(key);
    if (!res || res === key) return fallback;
    return res;
  };

  const resetForgotForm = () => {
    setForgotStep(1);
    setForgotOtp('');
    setForgotNewPass('');
    setForgotConfirmPass('');
    setForgotError('');
    setForgotInfo('');
    setForgotLoading(false);
  };

  const openForgot = () => {
    setForgotEmail((credentials.email || '').trim());
    resetForgotForm();
    setForgotOpen(true);
  };

  const closeForgot = () => {
    setForgotOpen(false);
    resetForgotForm();
    setForgotEmail('');
  };

  const handleChange = (e) => {
    setCredentials({ ...credentials, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessBanner('');

    if (!captchaInput.trim()) {
      setError(getT('auth.captchaRequired', 'Please enter the security code.'));
      return;
    }
    if (captchaInput.trim().toLowerCase() !== captchaCode.toLowerCase()) {
      setError(getT('auth.captchaInvalid', 'Invalid security code. Please try again.'));
      refreshCaptcha();
      return;
    }

    setLoading(true);
    try {
      const response = await authAPI.login(credentials);
      if (response.status === 200 && response?.data?.success === true) {
        const payload = response.data.payload;
        const token = payload.authenticationToken.token;
        const user = payload.user ?? payload;
        const adminUser = buildAdminUser(user);
        setAdminToken(token);
        setAdminUser(adminUser);
        localStorage.setItem('showWelcomeToast', 'true');

        const defaultRoute = getDefaultRouteForRole(adminUser.role);
        browserGlobal.location.href = defaultRoute;
      } else {
        setError(response.data?.message || t('auth.loginFailed'));
        refreshCaptcha();
        setLoading(false);
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(err.response?.data?.message || t('auth.loginFailedRetry'));
      refreshCaptcha();
      setLoading(false);
    }
  };

  const handleSendOtp = async () => {
    const email = forgotEmail.trim();
    if (!email) {
      setForgotError(t('auth.invalidEmail'));
      return;
    }
    setForgotError('');
    setForgotInfo('');
    setForgotLoading(true);
    try {
      const res = await authAPI.forgetPassword({ email });
      if (res?.data && res.data.success === false) {
        throw new Error(res.data.message || res.data.errorMessage || t('auth.resetFailed'));
      }
      setForgotInfo(t('auth.otpSent'));
      setForgotStep(2);
    } catch (err) {
      setForgotError(
        err?.response?.data?.message ||
        err?.response?.data?.errorMessage ||
        err?.message ||
        t('auth.resetFailed')
      );
    } finally {
      setForgotLoading(false);
    }
  };

  const handleResetPassword = async () => {
    const email = forgotEmail.trim();
    const otp = forgotOtp.trim();
    const newPassword = forgotNewPass;
    if (!email || !otp) {
      setForgotError(t('auth.resetFailed'));
      return;
    }
    if (!newPassword || newPassword !== forgotConfirmPass) {
      setForgotError(t('auth.passwordMismatch'));
      return;
    }
    setForgotError('');
    setForgotInfo('');
    setForgotLoading(true);
    try {
      const res = await authAPI.resetPasswordWithOtp({
        email,
        newPassword,
        otp,
      });
      if (res?.data && res.data.success === false) {
        throw new Error(res.data.message || res.data.errorMessage || t('auth.resetFailed'));
      }
      setCredentials((c) => ({ ...c, email }));
      setSuccessBanner(t('auth.resetSuccess'));
      closeForgot();
    } catch (err) {
      setForgotError(
        err?.response?.data?.message ||
        err?.response?.data?.errorMessage ||
        err?.message ||
        t('auth.resetFailed')
      );
    } finally {
      setForgotLoading(false);
    }
  };

  const isFormValid = Boolean(
    credentials.email?.trim() &&
    credentials.password?.trim() &&
    captchaInput?.trim()
  );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        maxWidth: '100vw',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 50%, #f1f5f9 100%)',
        position: 'relative',
        overflow: 'hidden',
        overflowX: 'hidden',
        boxSizing: 'border-box',
        px: 2,
        py: 4,
      }}
    >
      {/* Contained soft background ambient light shapes */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '300px',
          height: '300px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(15, 61, 62, 0.08) 0%, rgba(255,255,255,0) 70%)',
          filter: 'blur(40px)',
          pointerEvents: 'none',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          right: 0,
          width: '300px',
          height: '300px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(27, 90, 92, 0.08) 0%, rgba(255,255,255,0) 70%)',
          filter: 'blur(40px)',
          pointerEvents: 'none',
        }}
      />

      {/* Main Login Card with Rich Multi-Layer Shadow */}
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: '430px',
          boxSizing: 'border-box',
          borderRadius: '24px',
          p: { xs: 3, sm: 4.5 },
          bgcolor: '#ffffff',
          border: '1px solid #e2e8f0',
          boxShadow: '0 20px 45px -15px rgba(15, 61, 62, 0.15), 0 10px 20px -5px rgba(0, 0, 0, 0.04), 0 0 1px rgba(0, 0, 0, 0.1)',
          position: 'relative',
          zIndex: 1,
        }}
      >
        {/* Header Branding */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3.5 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 2,
            }}
          >
            <Box
              component="img"
              src={logo}
              alt="GetSettled Logo"
              sx={{
                maxHeight: '64px',
                maxWidth: '200px',
                objectFit: 'contain',
                display: 'block',
              }}
            />
          </Box>
          <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.88rem', fontWeight: 500 }}>
            Sign in to access your administrative portal
          </Typography>
        </Box>

        <Box component="form" onSubmit={handleSubmit} noValidate>
          {successBanner && (
            <Alert severity="success" sx={{ mb: 2.5, borderRadius: '12px', bgcolor: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0' }}>
              {successBanner}
            </Alert>
          )}

          {/* Email Field */}
          <Box sx={{ mb: 2.5 }}>
            <Typography variant="caption" sx={{ color: '#475569', fontWeight: 600, mb: 0.8, display: 'block' }}>
              {t('auth.email')}
            </Typography>
            <TextField
              required
              fullWidth
              id="email"
              name="email"
              placeholder="admin@getsettled.de"
              autoComplete="email"
              autoFocus
              value={credentials.email}
              onChange={handleChange}
              disabled={loading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailOutlined sx={{ color: '#64748b', fontSize: '1.2rem' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  bgcolor: '#f8fafc',
                  fontSize: '0.95rem',
                  transition: 'all 0.2s ease',
                  '& fieldset': { borderColor: '#e2e8f0' },
                  '&:hover fieldset': { borderColor: '#0F3D3E' },
                  '&.Mui-focused': {
                    bgcolor: '#fff',
                    boxShadow: '0 0 0 4px rgba(15, 61, 62, 0.08)',
                    '& fieldset': { borderColor: '#0F3D3E', borderWidth: '1.5px' },
                  },
                },
              }}
            />
          </Box>

          {/* Password Field */}
          <Box sx={{ mb: 1.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.8 }}>
              <Typography variant="caption" sx={{ color: '#475569', fontWeight: 600 }}>
                {t('auth.password')}
              </Typography>
              <Link
                component="button"
                type="button"
                variant="caption"
                onClick={openForgot}
                disabled={loading}
                underline="hover"
                sx={{
                  color: '#0F3D3E',
                  fontWeight: 700,
                  cursor: loading ? 'default' : 'pointer',
                  fontSize: '0.8rem',
                }}
              >
                {t('auth.forgotPassword')}
              </Link>
            </Box>
            <TextField
              required
              fullWidth
              name="password"
              placeholder="••••••••••••"
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="current-password"
              value={credentials.password}
              onChange={handleChange}
              disabled={loading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlined sx={{ color: '#64748b', fontSize: '1.2rem' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword((prev) => !prev)}
                      edge="end"
                      size="small"
                      sx={{ color: '#64748b' }}
                    >
                      {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  bgcolor: '#f8fafc',
                  fontSize: '0.95rem',
                  transition: 'all 0.2s ease',
                  '& fieldset': { borderColor: '#e2e8f0' },
                  '&:hover fieldset': { borderColor: '#0F3D3E' },
                  '&.Mui-focused': {
                    bgcolor: '#fff',
                    boxShadow: '0 0 0 4px rgba(15, 61, 62, 0.08)',
                    '& fieldset': { borderColor: '#0F3D3E', borderWidth: '1.5px' },
                  },
                },
              }}
            />
          </Box>

          {/* Security Captcha Card */}
          <Box
            sx={{
              bgcolor: '#f8fafc',
              p: 2,
              borderRadius: '16px',
              border: '1px solid #e2e8f0',
              boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.03)',
              mt: 2.5,
              mb: 2.5,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1.2 }}>
              <ShieldOutlined sx={{ color: '#0F3D3E', fontSize: '1.1rem' }} />
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#334155', letterSpacing: '0.2px' }}>
                {getT('auth.captchaLabel', 'SECURITY VERIFICATION')}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                margin="none"
                required
                fullWidth
                id="captchaInput"
                name="captchaInput"
                placeholder={getT('auth.enterCaptcha', 'Security Code')}
                variant="outlined"
                size="small"
                value={captchaInput}
                onChange={(e) => setCaptchaInput(e.target.value)}
                disabled={loading}
                autoComplete="off"
                sx={{
                  flex: 1,
                  '& .MuiOutlinedInput-root': {
                    height: 42,
                    borderRadius: '10px',
                    bgcolor: '#fff',
                    fontSize: '0.9rem',
                    '& fieldset': { borderColor: '#cbd5e1' },
                    '&:hover fieldset': { borderColor: '#0F3D3E' },
                    '&.Mui-focused fieldset': { borderColor: '#0F3D3E' },
                  },
                }}
              />
              <Box
                sx={{
                  border: '1px solid #cbd5e1',
                  borderRadius: '10px',
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  bgcolor: '#e2e8f0',
                  height: 42,
                  flexShrink: 0,
                  userSelect: 'none',
                }}
              >
                <canvas ref={canvasRef} width={100} height={42} style={{ display: 'block' }} />
              </Box>
              <Tooltip title={getT('auth.refreshCaptcha', 'Refresh Captcha')}>
                <IconButton
                  onClick={refreshCaptcha}
                  size="small"
                  disabled={loading}
                  sx={{
                    color: '#0F3D3E',
                    p: 1,
                    bgcolor: '#fff',
                    border: '1px solid #cbd5e1',
                    borderRadius: '10px',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.04)',
                    flexShrink: 0,
                    '&:hover': { bgcolor: 'rgba(15, 61, 62, 0.08)', borderColor: '#0F3D3E' },
                  }}
                >
                  <Refresh fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }}>
              {error}
            </Alert>
          )}

          {/* Submit Button with Rich Elevated Shadow */}
          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading || !isFormValid}
            sx={{
              py: 1.5,
              borderRadius: '12px',
              fontWeight: 700,
              textTransform: 'none',
              fontSize: '1rem',
              letterSpacing: '0.3px',
              bgcolor: '#0F3D3E',
              color: '#ffffff',
              boxShadow: '0 10px 22px -5px rgba(15, 61, 62, 0.45)',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                bgcolor: '#0a2829',
                transform: 'translateY(-2px)',
                boxShadow: '0 14px 28px -5px rgba(15, 61, 62, 0.55)',
              },
              '&.Mui-disabled': {
                background: '#e2e8f0',
                color: '#94a3b8',
                boxShadow: 'none',
              },
            }}
          >
            {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : t('auth.signIn')}
          </Button>
        </Box>

      </Paper>

      <Dialog
        open={forgotOpen}
        onClose={() => {
          if (!forgotLoading) closeForgot();
        }}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            maxWidth: '460px',
            width: '100%',
            borderRadius: '16px',
            p: 1.5,
            boxShadow: '0 20px 40px rgba(10, 40, 41, 0.15)',
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#0a2829', pb: 1, pt: 1.5, px: 3, fontSize: '1.35rem' }}>
          {t('auth.forgotDialogTitle')}
        </DialogTitle>
        <DialogContent sx={{ px: 3, pb: 2 }}>
          {forgotError && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }} onClose={() => setForgotError('')}>
              {forgotError}
            </Alert>
          )}
          {forgotInfo && forgotStep === 2 && (
            <Alert
              severity="info"
              icon={<InfoOutlined sx={{ color: '#3b82f6', fontSize: '1.3rem' }} />}
              sx={{
                mb: 2.5,
                borderRadius: '12px',
                bgcolor: '#eff6ff',
                color: '#2b435e',
                border: '1px solid #dbeafe',
                alignItems: 'center',
                '& .MuiAlert-message': {
                  fontSize: '0.88rem',
                  lineHeight: 1.5,
                  color: '#2b435e',
                },
              }}
            >
              {forgotInfo}
            </Alert>
          )}
          {forgotStep === 1 ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, lineHeight: 1.5, fontSize: '0.88rem' }}>
                {t('auth.forgotStepEmail')}
              </Typography>
              <TextField
                autoFocus
                fullWidth
                label={t('auth.email')}
                type="email"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                disabled={forgotLoading}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                    '& fieldset': { borderColor: '#cbd5e1' },
                    '&:hover fieldset': { borderColor: '#0F3D3E' },
                    '&.Mui-focused fieldset': { borderColor: '#0F3D3E' },
                  },
                  '& .MuiInputLabel-root': {
                    color: '#64748b',
                    '&.Mui-focused': { color: '#0F3D3E' },
                  },
                }}
              />
            </>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 0.5 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, lineHeight: 1.5, fontSize: '0.88rem' }}>
                {t('auth.forgotStepReset')}
              </Typography>
              <TextField
                fullWidth
                size="medium"
                label={t('auth.email')}
                type="email"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                disabled={forgotLoading}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                    '& fieldset': { borderColor: '#cbd5e1' },
                    '&:hover fieldset': { borderColor: '#0F3D3E' },
                    '&.Mui-focused fieldset': { borderColor: '#0F3D3E' },
                    '&.Mui-disabled': {
                      bgcolor: '#f8fafc',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: '#64748b',
                    '&.Mui-focused': { color: '#0F3D3E' },
                  },
                  '& .MuiInputBase-input.Mui-disabled': {
                    WebkitTextFillColor: '#1e293b',
                    fontWeight: 500,
                  },
                }}
              />
              <TextField
                fullWidth
                size="medium"
                label={t('auth.otp')}
                value={forgotOtp}
                onChange={(e) => setForgotOtp(e.target.value)}
                disabled={forgotLoading}
                autoComplete="one-time-code"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                    '& fieldset': { borderColor: '#cbd5e1' },
                    '&:hover fieldset': { borderColor: '#0F3D3E' },
                    '&.Mui-focused fieldset': { borderColor: '#0F3D3E' },
                  },
                  '& .MuiInputLabel-root': {
                    color: '#64748b',
                    '&.Mui-focused': { color: '#0F3D3E' },
                  },
                }}
              />
              <TextField
                fullWidth
                size="medium"
                label={t('auth.newPassword')}
                type={showNewPassword ? 'text' : 'password'}
                value={forgotNewPass}
                onChange={(e) => setForgotNewPass(e.target.value)}
                disabled={forgotLoading}
                autoComplete="new-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle new password visibility"
                        onClick={() => setShowNewPassword((prev) => !prev)}
                        edge="end"
                        size="small"
                        sx={{ color: '#64748b' }}
                      >
                        {showNewPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                    '& fieldset': { borderColor: '#cbd5e1' },
                    '&:hover fieldset': { borderColor: '#0F3D3E' },
                    '&.Mui-focused fieldset': { borderColor: '#0F3D3E' },
                  },
                  '& .MuiInputLabel-root': {
                    color: '#64748b',
                    '&.Mui-focused': { color: '#0F3D3E' },
                  },
                }}
              />
              <TextField
                fullWidth
                size="medium"
                label={t('auth.confirmNewPassword')}
                type={showConfirmPassword ? 'text' : 'password'}
                value={forgotConfirmPass}
                onChange={(e) => setForgotConfirmPass(e.target.value)}
                disabled={forgotLoading}
                autoComplete="new-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle confirm password visibility"
                        onClick={() => setShowConfirmPassword((prev) => !prev)}
                        edge="end"
                        size="small"
                        sx={{ color: '#64748b' }}
                      >
                        {showConfirmPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                    '& fieldset': { borderColor: '#cbd5e1' },
                    '&:hover fieldset': { borderColor: '#0F3D3E' },
                    '&.Mui-focused fieldset': { borderColor: '#0F3D3E' },
                  },
                  '& .MuiInputLabel-root': {
                    color: '#64748b',
                    '&.Mui-focused': { color: '#0F3D3E' },
                  },
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            pb: 2.5,
            pt: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {forgotStep === 2 && (
              <Button
                onClick={() => {
                  setForgotStep(1);
                  setForgotError('');
                  setForgotOtp('');
                  setForgotNewPass('');
                  setForgotConfirmPass('');
                }}
                disabled={forgotLoading}
                sx={{
                  color: '#0a2829',
                  fontWeight: 700,
                  textTransform: 'none',
                  fontSize: '0.9rem',
                  p: '6px 8px',
                  borderRadius: '8px',
                  '&:hover': { bgcolor: 'rgba(10, 40, 41, 0.06)' },
                }}
              >
                {t('auth.backToEmailStep')}
              </Button>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Button
              onClick={closeForgot}
              disabled={forgotLoading}
              sx={{
                color: '#0a2829',
                fontWeight: 700,
                textTransform: 'none',
                fontSize: '0.9rem',
                px: 2,
                borderRadius: '8px',
                '&:hover': { bgcolor: 'rgba(10, 40, 41, 0.06)' },
              }}
            >
              {t('common.cancel')}
            </Button>
            {forgotStep === 1 ? (
              <Button
                variant="contained"
                onClick={handleSendOtp}
                disabled={forgotLoading}
                sx={{
                  px: 3,
                  py: 1,
                  borderRadius: '10px',
                  fontWeight: 700,
                  textTransform: 'none',
                  fontSize: '0.9rem',
                  bgcolor: '#0F3D3E',
                  boxShadow: '0 4px 14px rgba(15, 61, 62, 0.35)',
                  '&:hover': {
                    bgcolor: '#0a2829',
                  },
                }}
              >
                {forgotLoading ? <CircularProgress size={22} sx={{ color: 'white' }} /> : t('auth.sendOtp')}
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleResetPassword}
                disabled={forgotLoading}
                sx={{
                  px: 3,
                  py: 1,
                  borderRadius: '10px',
                  fontWeight: 700,
                  textTransform: 'none',
                  fontSize: '0.9rem',
                  bgcolor: '#0F3D3E',
                  boxShadow: '0 4px 14px rgba(15, 61, 62, 0.35)',
                  '&:hover': {
                    bgcolor: '#0a2829',
                  },
                }}
              >
                {forgotLoading ? <CircularProgress size={22} sx={{ color: 'white' }} /> : t('auth.resetPassword')}
              </Button>
            )}
          </Box>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Login;
